// ─────────────────────────────────────────────
//  useBarDrag — the gesture that moves ONE edge of ONE bar on the Timeline.
//
//  Lifted OUT of `CaseBar` unchanged when the chart grew a second lane. There
//  are now two draggable bars in a paired room — the seat's case window above
//  and the second provider's own hours below — and they must behave
//  identically: the same 15-minute snap, the same sub-threshold wobble that
//  stays a click, the same one-shot click suppression after a real drag, the
//  same keyboard sliders. A second copy of this gesture is how the two lanes
//  would start disagreeing about what counts as a drag.
//
//  ⚠️ NO ARITHMETIC LIVES HERE. Every minute comes out of `domain/timelineDrag`
//  with the measured rect passed in. jsdom has no layout, so maths done in a
//  component is maths that cannot be tested.
//
//  ⚠️ THE CALLER OWNS THE STYLE. This hook returns behaviour and state only:
//  `handleProps(edge)` carries the ARIA, the pointer handlers and the keys, and
//  the lane adds `S.handle` on top. Styling from inside a hook would have made
//  the seat lane and the second lane silently diverge the first time one of
//  them needed a different inset.
// ─────────────────────────────────────────────
import { useCallback, useRef, useState } from "react";
import { DRAG_STEP_MIN, dragResult, clampDrag } from "../../domain/timelineDrag";
import { to12h } from "../../domain/timeline";

// How far the pointer must travel before this stops being a click.
//
// ⚠️ A RATIO, NOT A PIXEL COUNT. Wall mode puts a CSS `transform: scale()`
// (0.7–2.2) over this whole page; `clientX` and `getBoundingClientRect()` are
// both post-transform, so a stored "3px" would mean 2.1px on a shrunk board and
// 6.6px on a magnified one. A fraction of the measured track is the same
// physical distance at every zoom. 0.004 of a ~900px lane is ~3.6px.
export const DRAG_THRESHOLD_RATIO = 0.004;
export const DRAG_THRESHOLD_FLOOR = 2;

const SHIFT_HOUR = 60;

// Local "HH:MM". `minutesToHm` wraps past midnight; everything here is already
// clamped inside the drawn day by `clampDrag`.
export function hm(min) {
  const v = Math.max(0, Math.round(min));
  return `${String(Math.floor(v / 60)).padStart(2, "0")}:${String(v % 60).padStart(2, "0")}`;
}

export default function useBarDrag({
  draggable = false,
  axisStart,
  span,
  // The COMMITTED window this gesture starts from, in minutes.
  startMin,
  endMin,
  // Optional "HH:MM" fallbacks for a row whose minutes could not be derived.
  startTime = "",
  endTime = "",
  // (edge) => the slider's accessible name.
  labelFor,
  // ({ startTime, endTime, startMin, endMin, source }) => void
  onCommit,
}) {
  // The candidate window while a gesture is in flight, or null. Rendering from
  // it is the whole "ghost": nothing is written until release.
  const [ghost, setGhost] = useState(null);
  // Which edge a POINTER is currently holding, or null.
  const [dragEdge, setDragEdge] = useState(null);
  // Which edge the KEYBOARD is currently staging, or null. Arrow keys move it,
  // Enter commits, Escape (or losing focus) abandons it.
  const [kbdEdge, setKbdEdge] = useState(null);
  // ⚠️ FOCUS IS TRACKED IN STATE BECAUSE THESE ARE INLINE STYLES. There is no
  // `:focus-visible` to hook, and the browser default ring lands on a pale
  // section tint.
  const [focusEdge, setFocusEdge] = useState(null);

  const dragRef = useRef(null);
  // Set on a release that actually moved. The click event that follows a drag
  // would otherwise act on top of the change just made.
  //
  // ⚠️ THE ONE MECHANISM, SHARED. The name trigger reads this same ref before
  // it opens the quick-actions menu, so a drag that happens to end over a name
  // does not open a menu. A second suppression flag beside this one is how the
  // two would fall out of step.
  const suppressClickRef = useRef(false);

  const gesturing = dragEdge != null || kbdEdge != null;

  const computeFrom = useCallback(
    (d, clientX) =>
      dragResult({
        edge: d.edge,
        clientX,
        rect: d.rect,
        // ⚠️ FROZEN AT POINTERDOWN. `axisStart`/`span` are DERIVED from the
        // day's earliest and latest case (timeline.js), so dragging a bar past
        // the current last case re-scales the axis and every other bar on the
        // page slides under the cursor mid-gesture. The window is frozen with
        // them for the same reason.
        axisStart: d.axisStart,
        span: d.span,
        startMin: d.startMin,
        endMin: d.endMin,
        startTime: d.startTime,
        endTime: d.endTime,
      }),
    []
  );

  const onHandleDown = (edge) => (e) => {
    if (!draggable) return;
    // Secondary mouse buttons are the context menu, not a drag.
    if (e.pointerType === "mouse" && e.button !== 0) return;
    const trackEl = e.currentTarget.closest && e.currentTarget.closest("[data-tl-track]");
    const rect = trackEl ? trackEl.getBoundingClientRect() : null;
    // ⚠️ NO MEASUREMENT, NO GESTURE — and no `preventDefault`, so the click
    // still reaches the bar and the modal still opens. A lane of zero width
    // (jsdom, a collapsed column, a chart that has not laid out) would
    // otherwise divide by it.
    if (!rect || !(rect.width > 0)) return;

    e.stopPropagation();
    if (e.preventDefault) e.preventDefault();
    dragRef.current = {
      edge,
      pointerId: e.pointerId,
      startX: e.clientX,
      rect,
      axisStart,
      span,
      startMin,
      endMin,
      startTime,
      endTime,
      moved: false,
      threshold: Math.max(DRAG_THRESHOLD_FLOOR, rect.width * DRAG_THRESHOLD_RATIO),
    };
    setKbdEdge(null);
    setDragEdge(edge);
    // ⚠️ FEATURE-DETECTED. jsdom implements none of the Pointer Capture API, so
    // an unguarded call throws inside every component test that fires a
    // pointerdown.
    if (typeof e.currentTarget.setPointerCapture === "function") {
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* not captured; the handlers still fire */ }
    }
  };

  const onHandleMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    if (d.pointerId != null && e.pointerId != null && e.pointerId !== d.pointerId) return;
    if (!d.moved && Math.abs(e.clientX - d.startX) < d.threshold) return;
    d.moved = true;
    setGhost(computeFrom(d, e.clientX));
  };

  const endGesture = (e) => {
    const d = dragRef.current;
    dragRef.current = null;
    setDragEdge(null);
    setGhost(null);
    if (d && typeof e.currentTarget.releasePointerCapture === "function") {
      try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* never captured */ }
    }
    return d;
  };

  const onHandleUp = (e) => {
    const d = endGesture(e);
    if (!d) return;
    // Under the threshold this was a click, not a drag: say nothing, write
    // nothing, and let the bar's own onClick open the modal.
    if (!d.moved) return;
    // A real drag ended. The click that browsers synthesise next would land on
    // the bar (or on a name) and act on top of the change just made.
    suppressClickRef.current = true;
    const res = computeFrom(d, e.clientX);
    if (!res.changed) return;
    if (onCommit) {
      onCommit({
        startTime: res.startTime,
        endTime: res.endTime,
        startMin: res.startMin,
        endMin: res.endMin,
        source: "drag",
      });
    }
  };

  const onHandleCancel = (e) => {
    // The OS took the pointer (a scroll, a system gesture). Abandon silently —
    // the unchanged bar is already on screen.
    if (endGesture(e)) suppressClickRef.current = true;
  };

  // ── The keyboard path ────────────────────────────────────────────────────
  //
  // Not decoration. A drag with no keyboard equivalent is a function only a
  // steady hand and a mouse can reach.
  const nudge = (edge, deltaMin) => {
    const base = ghost || { startMin, endMin };
    const candidate = (edge === "start" ? base.startMin : base.endMin) + deltaMin;
    const next = clampDrag({
      edge,
      candidateMin: candidate,
      startMin: base.startMin,
      endMin: base.endMin,
    });
    setKbdEdge(edge);
    setGhost({ ...next, changed: true });
  };

  const onHandleKeyDown = (edge) => (e) => {
    if (!draggable) return;
    const step = e.shiftKey ? SHIFT_HOUR : DRAG_STEP_MIN;
    switch (e.key) {
      case "ArrowLeft":
      case "ArrowDown":
        e.preventDefault();
        e.stopPropagation();
        nudge(edge, -step);
        return;
      case "ArrowRight":
      case "ArrowUp":
        e.preventDefault();
        e.stopPropagation();
        nudge(edge, step);
        return;
      case "Escape":
        if (!ghost) return;
        e.preventDefault();
        e.stopPropagation();
        setGhost(null);
        setKbdEdge(null);
        return;
      case "Enter":
      case " ":
        // ⚠️ ALWAYS SWALLOWED. Enter on a handle must not fall through to the
        // bar and open the modal — the handle is the control that has focus.
        e.preventDefault();
        e.stopPropagation();
        if (!ghost) return;
        setGhost(null);
        setKbdEdge(null);
        if (ghost.startMin === startMin && ghost.endMin === endMin) return;
        if (onCommit) {
          onCommit({
            startTime: hm(ghost.startMin),
            endTime: hm(ghost.endMin),
            startMin: ghost.startMin,
            endMin: ghost.endMin,
            source: "keyboard",
          });
        }
        return;
      default:
    }
  };

  // Losing focus abandons a staged change rather than writing one nobody
  // confirmed. Tabbing away from a half-nudged handle must not page a phone.
  const onHandleBlur = () => {
    if (kbdEdge == null) return;
    setGhost(null);
    setKbdEdge(null);
  };

  const liveStart = ghost ? ghost.startMin : startMin;
  const liveEnd = ghost ? ghost.endMin : endMin;

  const handleProps = (edge) => ({
    role: "slider",
    tabIndex: 0,
    "aria-label": labelFor ? labelFor(edge) : `${edge} time`,
    "aria-valuemin": axisStart,
    "aria-valuemax": axisStart + span,
    "aria-valuenow": edge === "start" ? liveStart : liveEnd,
    "aria-valuetext": to12h(edge === "start" ? liveStart : liveEnd),
    onPointerDown: onHandleDown(edge),
    onPointerMove: onHandleMove,
    onPointerUp: onHandleUp,
    onPointerCancel: onHandleCancel,
    onKeyDown: onHandleKeyDown(edge),
    onFocus: () => setFocusEdge(edge),
    onBlur: (e) => {
      setFocusEdge(null);
      onHandleBlur(e);
    },
    // The bar underneath opens a modal. A handle click is not that.
    onClick: (e) => e.stopPropagation(),
  });

  return {
    ghost,
    liveStart,
    liveEnd,
    gesturing,
    activeEdge: dragEdge || kbdEdge,
    focusEdge,
    suppressClickRef,
    handleProps,
  };
}
